import type { ColumnConfig, SalesDataPoint, SalesMetrics, TableRow } from "../types"
import {
  promo_lift_dollars_for_datapoint,
  promo_lift_units_for_datapoint,
  resolve_display_metrics,
  select_primary_sales_rows,
} from "./forecast_measure_slice"
import { metrics_part_cases } from "./quantity_basis"

/** Builds per-period rows for the forecast table using optional retailer `metrics.detail` slice. */
export function build_monthly_data_from_points(
  salesData: SalesDataPoint[] | undefined,
  columnConfig: ColumnConfig
): TableRow["monthly_data"] {
  const monthlyData: TableRow["monthly_data"] = {}
  if (!Array.isArray(salesData)) {
    return monthlyData
  }
  // One primary row per period: forecast shadows volume_based_estimate etc.
  // (last-row-wins used to make the cell display a different row than edits changed).
  for (const dataPoint of select_primary_sales_rows(salesData)) {
    const periodKey = dataPoint.period?.period_key
    if (!periodKey) {
      continue
    }
    const r = resolve_display_metrics(dataPoint, columnConfig)
    const m = dataPoint.metrics || ({} as SalesDataPoint["metrics"])
    monthlyData[periodKey] = {
      store_count: m.store_count || 0,
      sku_store_count: m.sku_store_count,
      volume_dollars: r.volume_dollars,
      cases_sold: r.cases_sold,
      part_cases: metrics_part_cases(m),
      units_sold: r.units_sold,
      is_forecast: dataPoint.is_forecast || false,
      is_actual: dataPoint.is_actual || false,
      // Carried so a fill-forward can refuse to write over a locked period; nothing else reads it.
      is_locked: dataPoint.is_locked,
      compare_axis: dataPoint.compare_axis,
      forecast_metrics: dataPoint.forecast_metrics,
      actual_metrics: dataPoint.actual_metrics,
      variance: dataPoint.variance,
      detail: m.detail,
    }
  }
  return monthlyData
}

export function calculate_totals_from_points(
  salesData: SalesDataPoint[] | undefined,
  columnConfig: ColumnConfig
): {
  total_stores: number
  total_sku_stores: number
  total_volume: number
  total_cases: number
  /** Σ part-cases across periods, for a row whose whole cases total 0. */
  total_part_cases: number
  total_units: number
  total_promo_lift_dollars: number
  total_promo_lift_units: number
  average_stores_per_period: number
  average_volume_per_period: number
  average_cases_per_period: number
} {
  if (!Array.isArray(salesData) || salesData.length === 0) {
    return {
      total_stores: 0,
      total_sku_stores: 0,
      total_volume: 0,
      total_cases: 0,
      total_part_cases: 0,
      total_units: 0,
      total_promo_lift_dollars: 0,
      total_promo_lift_units: 0,
      average_stores_per_period: 0,
      average_volume_per_period: 0,
      average_cases_per_period: 0,
    }
  }
  const primary_rows = select_primary_sales_rows(salesData)
  // Doors are a concurrent reach, not a flow: 100 stores for 12 months is 100 stores,
  // so the Totals column takes the max across periods (same rule as `max_store_count`
  // up the tree) while dollars/cases/units sum. The average stays a true per-period mean.
  let stores_sum = 0
  const totals = primary_rows.reduce(
    (acc, dataPoint) => {
      const r = resolve_display_metrics(dataPoint, columnConfig)
      const m = dataPoint.metrics || ({} as SalesDataPoint["metrics"])
      const u = r.units_sold ?? 0
      stores_sum += m.store_count || 0
      return {
        total_stores: Math.max(acc.total_stores, m.store_count || 0),
        // Same concurrent-reach rule for the velocity denominator: max across periods of
        // each period's summed SKU distribution (falls back to doors on leaf rows).
        total_sku_stores: Math.max(acc.total_sku_stores, m.sku_store_count || m.store_count || 0),
        total_volume: acc.total_volume + r.volume_dollars,
        total_cases: acc.total_cases + r.cases_sold,
        total_part_cases: acc.total_part_cases + metrics_part_cases(m),
        total_units: acc.total_units + u,
        total_promo_lift_dollars:
          acc.total_promo_lift_dollars + promo_lift_dollars_for_datapoint(dataPoint, columnConfig),
        total_promo_lift_units: acc.total_promo_lift_units + promo_lift_units_for_datapoint(dataPoint),
      }
    },
    {
      total_stores: 0,
      total_sku_stores: 0,
      total_volume: 0,
      total_cases: 0,
      total_part_cases: 0,
      total_units: 0,
      total_promo_lift_dollars: 0,
      total_promo_lift_units: 0,
    }
  )
  const periodCount = primary_rows.length
  return {
    ...totals,
    average_stores_per_period: periodCount > 0 ? stores_sum / periodCount : 0,
    average_volume_per_period: periodCount > 0 ? totals.total_volume / periodCount : 0,
    average_cases_per_period: periodCount > 0 ? totals.total_cases / periodCount : 0,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("calculate_totals_from_points store counts", () => {
    function dp(period_key: string, store_count: number): SalesDataPoint {
      return {
        period: { period_key, year: 2026, month: Number(period_key.slice(5)) },
        metrics: { store_count, volume_dollars: 1000, cases_sold: 10, case_cost: 0 },
      }
    }

    it("reports doors as the max across periods, not the sum", () => {
      const config: ColumnConfig = { show_stores: true, show_cases: true, show_volume: true }
      const totals = calculate_totals_from_points(
        [dp("2026-01", 100), dp("2026-02", 100), dp("2026-03", 100)],
        config
      )
      // A row at a flat 100 doors is in 100 stores, not 300.
      expect(totals.total_stores).toBe(100)
      expect(totals.average_stores_per_period).toBe(100)
      expect(totals.total_volume).toBe(3000)
    })

    it("averages doors over periods when the count varies", () => {
      const config: ColumnConfig = { show_stores: true, show_cases: true, show_volume: true }
      const totals = calculate_totals_from_points([dp("2026-01", 50), dp("2026-02", 150)], config)
      expect(totals.total_stores).toBe(150)
      expect(totals.average_stores_per_period).toBe(100)
    })
  })

  describe("build_monthly_data_from_points detail", () => {
    it("copies metrics.detail onto period cells", () => {
      const metrics: SalesMetrics = {
        store_count: 1,
        volume_dollars: 0,
        cases_sold: 0,
        case_cost: 0,
        detail: { forecasted_consumption_units: 100, incremental_promotion_consumption_units: 5 },
      }
      const dp: SalesDataPoint = {
        period: { period_key: "2026-W01", year: 2026, month: 1 },
        metrics,
      }
      const grid = build_monthly_data_from_points([dp], {
        show_stores: true,
        show_cases: true,
        show_volume: true,
        metric_mode: "units",
      })
      expect(grid["2026-W01"]?.detail?.forecasted_consumption_units).toBe(100)
    })
  })
}
